import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwiseIcon,
  Browser,
  Browsers,
  CheckCircle,
  Copy,
  FilmStrip,
  TerminalWindow,
  Trash,
  WarningCircle,
  XCircle
} from "@phosphor-icons/react";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  type BrowserObservationEvent,
  type BrowserObservationLevel,
  type BrowserObservationSnapshot
} from "@workerstation/shared";
import { useObservations } from "./react.tsx";
import { emptyObservationInput } from "./model.ts";
import { eventDetails } from "./format.ts";
import {
  formatResourceBytes,
  ResourceTreeAction,
  ResourceTreeRow
} from "../ResourceExplorerTree";

interface BrowserObservabilityExplorerProps {
  stationId: string | null;
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
}

const EMPTY_SNAPSHOT: BrowserObservationSnapshot = {
  events: [],
  sessions: [],
  stats: {
    eventCount: 0,
    sessionCount: 0,
    errorCount: 0,
    warningCount: 0,
    commandCount: 0,
    eventBytes: 0,
    categories: [],
    levels: []
  },
  generatedAt: 0
};

function shortTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3
  });
}

function eventIcon(event: BrowserObservationEvent) {
  if (event.level === "error") {
    return <XCircle size={13} weight="fill" className="text-red-500" />;
  }
  if (event.level === "warning") {
    return <WarningCircle size={13} weight="fill" className="text-amber-500" />;
  }
  if (event.kind === "cdp-command") {
    return <ArrowRight size={13} className="text-sky-500" />;
  }
  if (event.kind === "cdp-response") {
    return <ArrowLeft size={13} className="text-violet-500" />;
  }
  if (event.category === "runtime" || event.category === "log") {
    return <TerminalWindow size={13} className="text-emerald-500" />;
  }
  return <CheckCircle size={13} weight="fill" className="text-emerald-500" />;
}

export function BrowserObservabilityExplorer({
  stationId,
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange
}: BrowserObservabilityExplorerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["connection", "events"]));
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState<"all" | BrowserObservationLevel>("all");
  const [category, setCategory] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [live, setLive] = useState(false);
  const owned=useObservations({...emptyObservationInput("browser"),workspaceId,stationId,collapsed,refreshToken:refreshToken??0,search,level,category,sessionId,live});
  const snapshot=owned.snapshot.browser??EMPTY_SNAPSHOT,{loading,error}=owned.snapshot;
  useEffect(()=>{setExpanded(new Set(["connection","events"]));setSelectedEvent(null);setSearch("");setLevel("all");setCategory("");setSessionId("");},[workspaceId]);
  useEffect(()=>{setSelectedEvent(null);},[owned.snapshot.clearVersion]);
  const refresh=()=>owned.dispatch({_tag:"Refresh"});
  const clear=()=>owned.dispatch({_tag:"Clear"});

  const toggle = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selected = useMemo(
    () => snapshot.events.find((event) => event.id === selectedEvent),
    [selectedEvent, snapshot.events]
  );

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            <Browsers size={12} /> Browser observability
          </button>
        )}
        <button
          type="button"
          aria-pressed={live}
          title={live ? "Disable live event refresh" : "Enable live event refresh"}
          onClick={() => setLive((value) => !value)}
          className={`mr-0.5 h-4 rounded px-1 text-[8px] font-semibold uppercase ${
            live
              ? "bg-emerald-500/15 text-emerald-500"
              : "bg-kumo-elevated text-kumo-subtle"
          }`}
        >
          live
        </button>
        <ResourceTreeAction
          label="Refresh Browser observability"
          onClick={() => void refresh()}
          disabled={!workspaceId || loading}
        >
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
        <ResourceTreeAction
          label="Clear Browser observability"
          onClick={() => void clear()}
          disabled={!workspaceId || loading}
        >
          <Trash size={13} />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <>
          <div className="grid shrink-0 grid-cols-2 gap-1 border-b border-kumo-line/60 p-1">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search method, URL, summary…"
              aria-label="Search Browser observations"
              className="col-span-2 h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1.5 text-[10px] text-kumo-default outline-none focus:border-kumo-accent"
            />
            <select
              value={level}
              onChange={(event) =>
                setLevel(
                  event.target.value as "all" | BrowserObservationLevel
                )
              }
              aria-label="Filter Browser observation level"
              className="h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1 text-[10px] text-kumo-default"
            >
              <option value="all">all levels</option>
              <option value="debug">debug</option>
              <option value="info">info</option>
              <option value="warning">warning</option>
              <option value="error">error</option>
            </select>
            <input
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              placeholder="Category…"
              aria-label="Filter Browser observation category"
              className="h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1.5 text-[10px] text-kumo-default outline-none focus:border-kumo-accent"
            />
            <select
              value={sessionId}
              onChange={(event) => setSessionId(event.target.value)}
              aria-label="Filter Browser session"
              className="col-span-2 h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1 text-[10px] text-kumo-default"
            >
              <option value="">all sessions</option>
              {snapshot.sessions.map((session) => (
                <option key={session.sessionId} value={session.sessionId}>
                  {session.sessionId} · {session.eventCount} events
                </option>
              ))}
            </select>
          </div>
          <div className="grid shrink-0 grid-cols-4 gap-px border-b border-kumo-line/60 bg-kumo-line/60 text-center text-[9px]">
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong className="block text-xs text-kumo-default">
                {snapshot.stats.eventCount}
              </strong>
              events
            </div>
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong className="block text-xs text-kumo-default">
                {snapshot.stats.sessionCount}
              </strong>
              sessions
            </div>
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong
                className={
                  snapshot.stats.errorCount
                    ? "block text-xs text-red-500"
                    : "block text-xs text-kumo-default"
                }
              >
                {snapshot.stats.errorCount}
              </strong>
              errors
            </div>
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong className="block text-xs text-kumo-default">
                {formatResourceBytes(snapshot.stats.eventBytes)}
              </strong>
              stored
            </div>
          </div>
          <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
            {workspaceId ? (
              <>
                <ResourceTreeRow
                  depth={0}
                  label={connectionName || "Workerspace"}
                  detail="all CDP messages · sanitized"
                  icon={<Browser size={14} />}
                  expandable
                  expanded={expanded.has("connection")}
                  onClick={() => toggle("connection")}
                />
                {expanded.has("connection") ? (
                  <>
                    <ResourceTreeRow
                      depth={1}
                      label="Sessions"
                      detail={String(snapshot.sessions.length)}
                      icon={<Browsers size={14} />}
                      expandable
                      expanded={expanded.has("sessions")}
                      onClick={() => toggle("sessions")}
                    />
                    {expanded.has("sessions")
                      ? snapshot.sessions.map((session) => (
                          <ResourceTreeRow
                            key={session.sessionId}
                            depth={2}
                            label={session.sessionId}
                            detail={`${session.eventCount} events · ${session.errorCount} errors`}
                            title={`${session.bindingName}${session.workerName ? ` · ${session.workerName}` : ""}\n${new Date(session.firstSeen).toLocaleString()} — ${new Date(session.lastSeen).toLocaleString()}`}
                            icon={<Browser size={13} />}
                            onClick={() => setSessionId(session.sessionId)}
                            actions={
                              <ResourceTreeAction
                                label={`View recordings for session ${session.sessionId}`}
                                onClick={() =>
                                  owned.dispatch({_tag:"OpenRecording",sessionId:session.sessionId})
                                }
                              >
                                <FilmStrip size={12} />
                              </ResourceTreeAction>
                            }
                          />
                        ))
                      : null}
                    <ResourceTreeRow
                      depth={1}
                      label="Event stream"
                      detail={`${snapshot.events.length} loaded`}
                      icon={<TerminalWindow size={14} />}
                      expandable
                      expanded={expanded.has("events")}
                      onClick={() => toggle("events")}
                    />
                    {expanded.has("events")
                      ? snapshot.events.map((event) => (
                          <Fragment key={event.id}>
                            <ResourceTreeRow
                              depth={2}
                              label={event.method}
                              detail={`${shortTime(event.timestamp)} · ${event.category}`}
                              icon={eventIcon(event)}
                              onClick={() =>
                                setSelectedEvent((current) =>
                                  current === event.id ? null : event.id
                                )
                              }
                              title={event.summary}
                              actions={
                                <ResourceTreeAction
                                  label={`Copy Browser event ${event.id}`}
                                  onClick={() =>
                                    owned.dispatch({_tag:"CopyEvent",eventId:event.id})
                                  }
                                >
                                  <Copy size={12} />
                                </ResourceTreeAction>
                              }
                            />
                            {selected?.id === event.id ? (
                              <div className="mx-2 mb-1 rounded border border-kumo-line bg-kumo-elevated/40">
                                <p className="border-b border-kumo-line px-2 py-1 text-[9px] leading-4 text-kumo-default">
                                  {event.summary}
                                </p>
                                <pre className="max-h-72 overflow-auto p-2 text-[9px] leading-4 text-kumo-subtle">
                                  {JSON.stringify(eventDetails(event), null, 2)}
                                </pre>
                              </div>
                            ) : null}
                          </Fragment>
                        ))
                      : null}
                  </>
                ) : null}
              </>
            ) : (
              <p className="p-2 text-[11px] text-kumo-subtle">
                Open a workerspace to inspect Browser observability.
              </p>
            )}
            {!snapshot.events.length && !loading && workspaceId ? (
              <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                No Browser observations yet. Run code using a Browser binding;
                DevTools page, console, runtime, network, target, session, and
                quick-action events will appear here.
              </div>
            ) : null}
            {error ? (
              <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
                {error}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
